<template>
    <div class="container">
        <schoolAndDate :schoolName="schoolName" :dateTime="dateTime" />
        <div class="wrap" ref="viewport">
            <transition name="page-turn-fade" mode="out-in">
                <div class="meal-list-wrap" :key="currentPageKey">
                    <div class="meal-list" v-for="(section, sectionIndex) in currentPage.sections" :key="section.sectionKey + '-' + sectionIndex">
                        <div class="meal-date">{{ section.sectionHeader.endDate || '' }} {{ section.sectionHeader.startDate || '' }}</div>
                        <div class="meal-block">
                            <div class="meal-item" v-for="row in section.rows" :key="row.rowKey">
                                <div class="meal-title">
                                    <span>{{ row.mealLabel }}</span>
                                    <span>{{ row.changeText }}</span>
                                </div>
                                <div class="meal-content">{{ row.contentText }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>

            <div class="measure-layer" aria-hidden="true">
                <div class="meal-list-wrap measure-wrap" ref="measureContent">
                    <div class="meal-list" v-for="(section, sectionIndex) in normalizedSections" :key="section.sectionKey + '-measure'" :ref="'measureSection-' + sectionIndex">
                        <div class="meal-date" :ref="'measureHeader-' + sectionIndex">{{ section.sectionHeader.endDate || '' }} {{ section.sectionHeader.startDate || '' }}</div>
                        <div class="meal-block">
                            <div class="meal-item" v-for="(row, rowIndex) in section.rows" :key="row.rowKey" :ref="'measureRow-' + sectionIndex + '-' + rowIndex">
                                <div class="meal-title">
                                    <span>{{ row.mealLabel }}</span>
                                    <span>{{ row.changeText }}</span>
                                </div>
                                <div class="meal-content">{{ row.contentText }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <PageTurnControl class="page-turn-control-position" :current="currentPageIndex + 1" :total="pages.length" theme="meal" @prev="goPrevPage" @next="goNextPage" />
        </div>
    </div>
</template>

<script>
import PageTurnControl from './PageTurnControl.vue';
import schoolAndDate from './schoolAndDate.vue';
import { buildOneWeekUnifiedMealSections } from '../assets/js/oneWeekUnifiedMealSections';
import { paginateMeasuredSections } from '../assets/js/contentPaging';
import { canAutoTurnPage, getNextPageIndex, getPrevPageIndex } from '../assets/js/pageTurn';

export default {
    props: {
        deptId: {
            type: String,
            default: ''
        },
        outletsId: {
            type: String,
            default: ''
        },
        tjcData: {
            type: Array,
            default: function () {
                return [];
            }
        },
        dateTime: {
            type: String,
            default: '',
        },
        schoolName: {
            type: String,
            default: '',
        },
    },
    components: { PageTurnControl, schoolAndDate },
    data() {
        return {
            infos: [],
            pages: [],
            currentPageIndex: 0,
            pageTimer: null,
        };
    },
    computed: {
        normalizedSections() {
            return buildOneWeekUnifiedMealSections({
                infos: this.infos,
                tjcData: this.tjcData,
            });
        },
        currentPage() {
            return this.pages[this.currentPageIndex] || { sections: [] };
        },
        currentPageKey() {
            return 'page-' + this.currentPageIndex;
        },
        autoplayDuration() {
            var query = this.$route && this.$route.query ? this.$route.query : {};
            var autoplay = query && query.autoplay ? parseInt(query.autoplay, 10) : 0;

            if (isNaN(autoplay) || autoplay <= 0) {
                return 0;
            }

            return autoplay;
        },
    },
    watch: {
        infos: {
            deep: true,
            handler: function () {
                this.rebuildPages();
            },
        },
        tjcData: {
            deep: true,
            handler: function () {
                this.rebuildPages();
            },
        },
        '$route.query.autoplay': function () {
            this.startPageTimer();
        },
    },
    mounted() {
        this.getMenuPriceAnnouncementList();
        window.addEventListener('resize', this.rebuildPages);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.rebuildPages);
        this.stopPageTimer();
    },
    methods: {
        getMenuPriceAnnouncementList() {
            var param = {
                outletsId: this.outletsId,
                recipeType: 0
            };
            this.$get(this.$api.getMenuPriceAnnouncementList, param).then((res) => {
                this.infos = Array.isArray(res && res.data) ? res.data : [];
            });
        },
        stopPageTimer() {
            if (this.pageTimer) {
                clearInterval(this.pageTimer);
                this.pageTimer = null;
            }
        },
        startPageTimer() {
            var vm = this;
            this.stopPageTimer();
            if (!canAutoTurnPage(this.pages.length, this.autoplayDuration)) {
                return;
            }
            this.pageTimer = setInterval(function () {
                vm.currentPageIndex = getNextPageIndex(vm.currentPageIndex, vm.pages.length);
            }, this.autoplayDuration);
        },
        restartPageTimer() {
            this.stopPageTimer();
            this.startPageTimer();
        },
        goPrevPage() {
            this.currentPageIndex = getPrevPageIndex(this.currentPageIndex, this.pages.length);
            this.restartPageTimer();
        },
        goNextPage() {
            this.currentPageIndex = getNextPageIndex(this.currentPageIndex, this.pages.length);
            this.restartPageTimer();
        },
        getRefElement(refName) {
            var refValue = this.$refs[refName];
            return Array.isArray(refValue) ? refValue[0] : refValue;
        },
        getMeasuredSection(section, sectionIndex) {
            var headerEl = this.getRefElement('measureHeader-' + sectionIndex);
            var sectionEl = this.getRefElement('measureSection-' + sectionIndex);
            var rows = section.rows.map(function (row, rowIndex) {
                var rowEl = this.getRefElement('measureRow-' + sectionIndex + '-' + rowIndex);
                return {
                    rowKey: row.rowKey,
                    measuredHeight: rowEl ? rowEl.offsetHeight : 0,
                    payload: row,
                };
            }, this);
            var maxRowHeight = rows.reduce(function (maxHeight, row) {
                return row.measuredHeight > maxHeight ? row.measuredHeight : maxHeight;
            }, 0);
            var totalSectionHeight = sectionEl ? sectionEl.offsetHeight : 0;
            var headerHeight = totalSectionHeight > maxRowHeight ? totalSectionHeight - maxRowHeight : (headerEl ? headerEl.offsetHeight : 0);

            return {
                sectionKey: section.sectionKey,
                sectionHeader: section.sectionHeader,
                headerHeight: headerHeight,
                rowLayout: 'horizontal',
                rows: rows,
            };
        },
        getMeasureWrapInset() {
            var measureContent = this.$refs.measureContent;
            var styles;

            if (!measureContent) {
                return 0;
            }

            styles = window.getComputedStyle(measureContent);
            return (parseFloat(styles.paddingTop) || 0) + (parseFloat(styles.paddingBottom) || 0);
        },
        getSectionGap() {
            var firstSection = this.getRefElement('measureSection-0');
            var styles;

            if (!firstSection) {
                return 0;
            }

            styles = window.getComputedStyle(firstSection);
            return parseFloat(styles.marginBottom) || 0;
        },
        rebuildPages() {
            this.$nextTick(function () {
                var viewport = this.$refs.viewport;
                var maxPageHeight = viewport ? viewport.clientHeight : 0;
                var pageInset = this.getMeasureWrapInset();
                var sectionGap = this.getSectionGap();
                var measuredSections = this.normalizedSections.map(function (section, index) {
                    return this.getMeasuredSection(section, index);
                }, this);

                this.pages = paginateMeasuredSections({
                    maxPageHeight: maxPageHeight,
                    pageInset: pageInset,
                    sectionGap: sectionGap,
                    sections: measuredSections,
                }).map(function (page) {
                    return {
                        pageIndex: page.pageIndex,
                        sections: page.sections.map(function (section) {
                            return {
                                sectionKey: section.sectionKey,
                                sectionHeader: section.sectionHeader,
                                continued: section.continued,
                                rows: section.rows.map(function (row) {
                                    return row.payload;
                                }),
                            };
                        }),
                    };
                });

                this.currentPageIndex = 0;
                this.startPageTimer();
            });
        },
    }
};
</script>

<style lang="scss" scoped>
$fontColor: #0A7B23;
$borderColor: #59C06F;
$wrapHeight: 830;

.container {
    position: relative;
}

.wrap {
    position: relative;
    height: vh($wrapHeight);
    width: 100%;
    overflow: hidden;
    border-radius: 3px;
    font-weight: bold;
    background: #b2f5c2;
}

.meal-list-wrap {
    padding: vh(12) vh(18) vh(58);
}

.page-turn-control-position {
    position: absolute;
    left: 50%;
    bottom: vh(8);
    z-index: 8;
    transform: translateX(-50%);
}

.meal-list {
    margin-bottom: vh(20);
}

.meal-date {
    color: #000;
    text-align: left;
    font-size: vh(22);
    margin-bottom: vh(10);
}

.meal-block {
    overflow: hidden;
    display: flex;
    border: 1px solid $borderColor;
    color: $fontColor;
    border-radius: vh(10);
}

.meal-item {
    flex: 1;
    font-size: vh(16);
    white-space: normal;
    text-align: left;

    &:nth-of-type(2) {
        border-left: 1px solid $borderColor;
        border-right: 1px solid $borderColor;
    }
}

.meal-title {
    font-size: vh(14);
    padding: vh(6) 0;
    text-align: center;
    background: #8EDE9F;

    > span:nth-of-type(1) {
        margin-right: vh(12);
    }
}

.meal-content {
    word-wrap: break-word;
    padding: vh(10) vh(12);
}

.measure-layer {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    visibility: hidden;
    pointer-events: none;
    z-index: -1;
}

.measure-wrap {
    padding: vh(12) vh(18);
}

.page-turn-fade-enter-active,
.page-turn-fade-leave-active {
    transition: opacity 0.45s ease, transform 0.45s ease;
}

.page-turn-fade-enter,
.page-turn-fade-leave-to {
    opacity: 0;
    transform: translateX(vh(24));
}
</style>
